/**
 * board 2788 M1 (DD-5, R-7) - WHY A BOOKING'S DATE AND TIME WERE CHANGED, AS A
 * CLOSED LIST, and the codes the reschedule route refuses with.
 *
 * ⛔ A CLOSED LIST AND NEVER FREE TEXT. The reason lands in `booking_events`,
 * which is append-only and has no erasure path for an account booking, so the
 * house rule (`core/cancel-booking.ts`, "NO CANCELLATION REASON IS RECORDED")
 * applies: the operator picks a token, the token is stored, and the screen owns
 * the words. Nothing typed by a person reaches the row.
 *
 * ⭐ IN `src/shared` SO THE SCREEN AND THE SERVER READ ONE LIST. The server
 * module pulls in `pg`, which is why older refusal codes are hand-mirrored in
 * `client/lib/actions.ts`; this one needs no mirror.
 *
 * ⚠️ 'home_asked' is written automatically when Helen accepts a home's ask
 * (board 2788 M4). The kiosk form does not offer it, because Helen's own change
 * is never the home's ask.
 */
export const RESCHEDULE_REASONS = ['customer_asked', 'home_asked', 'our_change', 'correction'] as const;
export type RescheduleReason = (typeof RESCHEDULE_REASONS)[number];

/** The words for each token. Flagged for Vicky's sitting (DD-5). */
export const RESCHEDULE_REASON_LABELS: Readonly<Record<RescheduleReason, string>> = {
  customer_asked: 'The customer asked',
  home_asked: 'The home asked',
  our_change: 'We had to move it',
  correction: 'Correcting a mistake',
};

/** The tokens the kiosk form offers, in the order it offers them. */
export const KIOSK_RESCHEDULE_REASONS: readonly RescheduleReason[] = ['customer_asked', 'our_change', 'correction'];

/** Membership of the closed list, never a key lookup, so `'toString'` is not a reason. */
export function isRescheduleReason(v: unknown): v is RescheduleReason {
  return typeof v === 'string' && (RESCHEDULE_REASONS as readonly string[]).includes(v);
}

/**
 * Why a reschedule is refused (DD-4, S15). A CODE on the wire, never prose: the
 * screen keys its own sentence off it (SEC-5).
 *   - 'cancelled' / 'completed' : nothing to move.
 *   - 'series_run'              : a generated occurrence; the generator would
 *                                 re-create the vacated date and bill it twice.
 *   - 'undated'                 : no date to move (a repeating trip).
 *   - 'portal_linked'           : the trip came from the home's portal (lifted in M4).
 *   - 'date_passed'             : the trip's own date is already behind us (Dublin).
 *   - 'travelled'               : the run is marked travelled.
 *   - 'job_invoice_paid'        : a paid whole-job invoice or a live receipt carries the old date.
 *   - 'account_invoiced'        : the run is on an issued account invoice (named alongside).
 *   - 'new_date_past'           : the new date is past, or today at or before now.
 *   - 'too_far_ahead'           : more than two years ahead (a typo guard).
 *   - 'after_return'            : the new time would be after the stated return.
 *   - 'clash'                   : the database refused the new date as a duplicate.
 */
export const RESCHEDULE_REFUSALS = [
  'cancelled',
  'completed',
  'series_run',
  'undated',
  'portal_linked',
  'date_passed',
  'travelled',
  'job_invoice_paid',
  'account_invoiced',
  'new_date_past',
  'too_far_ahead',
  'after_return',
  'clash',
] as const;
export type RescheduleRefusal = (typeof RESCHEDULE_REFUSALS)[number];

export function isRescheduleRefusal(v: unknown): v is RescheduleRefusal {
  return typeof v === 'string' && (RESCHEDULE_REFUSALS as readonly string[]).includes(v);
}

/** `HH:MM`, 24-hour, the shape the kiosk's time input sends and the crossing writes. */
export const RESCHEDULE_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * ⭐ Board 2788 M4 (plan step 5) - why a PORTAL-LINKED booking's change is refused
 * BEFORE anything is written, as a CODE (SEC-5). Separate from
 * `RESCHEDULE_REFUSALS` because these come from the home's request, not the
 * booking, and every refusal there still applies to a linked booking too.
 *   - 'request_changed' : the home changed the request since it was read (DD-1).
 *   - 'cancel_asked'    : the home has asked to cancel; that is answered first (DD-8).
 *   - 'portal_refused'  : the request cannot take that day and time (the past,
 *                         beyond 28 days, a return it would pass, or its status).
 *   - 'portal_missing'  : the request is not on the portal, or the portal cannot
 *                         take a change yet (an image older than the ops door).
 *   - 'no_collection'   : the portal derived no collection time to book.
 */
export const LINKED_RESCHEDULE_REFUSALS = [
  'request_changed',
  'cancel_asked',
  'portal_refused',
  'portal_missing',
  'no_collection',
] as const;
export type LinkedRescheduleRefusal = (typeof LINKED_RESCHEDULE_REFUSALS)[number];

export function isLinkedRescheduleRefusal(v: unknown): v is LinkedRescheduleRefusal {
  return typeof v === 'string' && (LINKED_RESCHEDULE_REFUSALS as readonly string[]).includes(v);
}

/**
 * ⭐ Board 2788 M4 (DD-18) - what became of the HOME'S REQUEST after the kiosk
 * side of a linked change or an accept was decided:
 *   - 'changed' / 'already' : the request holds the new time.
 *   - 'not_reached'         : the portal answered before writing (provably unchanged).
 *   - 'unknown'             : a timeout or a failure where it may or may not have changed.
 *   - 'request_changed'     : the home changed the request in between, so it was not written.
 * Every arm but the first two tells Helen to press again; none is a quiet success.
 */
export const LINKED_PORTAL_OUTCOMES = ['changed', 'already', 'not_reached', 'unknown', 'request_changed'] as const;
export type LinkedPortalOutcome = (typeof LINKED_PORTAL_OUTCOMES)[number];

export function isLinkedPortalOutcome(v: unknown): v is LinkedPortalOutcome {
  return typeof v === 'string' && (LINKED_PORTAL_OUTCOMES as readonly string[]).includes(v);
}
